import * as React from 'react';
import { useRef } from 'react';
import { TextVariant } from '../Text/Text.js';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { textFieldVariants, inputStyle, vars, titleStyle, prefixStyle, prefixIconStyle, clearButtonStyle, suffixTextStyle, suffixIconStyle, hintStyle, componentStyle } from './styles.css.js';
import '@greenlabs/formula-design-token';
import { jsxs, jsx } from 'react/jsx-runtime';
import Memo from '../Icon/generated/DeleteFill.js';
import { stateClass } from './common.js';
import { triggerNativeEventFor } from '../utils/event.js';
const TextField = /*#__PURE__*/React.forwardRef(({
  id,
  className = "",
  name,
  renderInput,
  inputTag: InputTag = "input",
  props = {},
  placeholder,
  size = "medium",
  prefix,
  prefixIcon,
  suffix,
  suffixIcon,
  titleText,
  hintText,
  variant = "boxOutline",
  type = "text",
  readOnly,
  disabled,
  state,
  minLength,
  maxLength,
  pattern,
  defaultValue,
  value,
  inputMode,
  onInput,
  onKeyDown,
  onKeyUp,
  onPaste,
  onBlur,
  onChange,
  onFocus,
  required,
  form,
  autoFocus,
  options = {
    hideClearButton: false,
    showHintOnFocusOnly: false
  }
}, forwardedRef) => {
  const variantKey = `${variant}.${size}`;

  if (process.env.NODE_ENV !== "production" && !(variantKey in textFieldVariants)) {
    console.error(`You have used non-exist variant key ${size}.`);
  }

  const containerClass = textFieldVariants[variantKey] ?? "";
  const componentClass = componentStyle[variantKey] ?? "";
  const inputRef = useRef(null);
  React.useImperativeHandle(forwardedRef, () => inputRef.current); // TODO: refactor

  let titleVariantKey = size !== "xsmall" ? "body-md-bold" : "body-sm-bold";

  if (variant === "line") {
    titleVariantKey = "body-sm-medium";
  }

  const hintVariantKey = size === "large" || size === "medium" ? "body-sm-regular" : "caption-xs-regular";
  const PrefixIcon = prefixIcon;
  const SuffixIcon = suffixIcon;
  const inaccesibleIconColor = readOnly || disabled ? "gray-40" : undefined;
  const iconSize = size === "large" || size === "medium" ? "xl" : "lg";
  const innerId = React.useId();
  const inputProps = {
    id: id ?? innerId,
    className: inputStyle,
    name,
    type,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    readOnly,
    disabled,
    minLength,
    maxLength,
    pattern,
    defaultValue,
    value,
    inputMode,
    required,
    form,
    autoFocus,
    onInput,
    onKeyDown,
    onKeyUp,
    onPaste,
    ...props
  };
  return /*#__PURE__*/jsxs("label", {
    className: `${componentClass} ${className} ${stateClass({
      disabled,
      readOnly,
      error: state === "error",
      variantLine: variant === "line"
    })}`,
    htmlFor: id ?? innerId,
    style: options.showHintOnFocusOnly ? assignInlineVars({
      [vars.optionalHintColor]: "transparent"
    }) : undefined,
    children: [titleText ? /*#__PURE__*/jsx(TextVariant, {
      variantKey: titleVariantKey,
      className: titleStyle,
      children: titleText
    }) : null, /*#__PURE__*/jsxs("span", {
      className: containerClass,
      children: [prefix ? /*#__PURE__*/jsx("span", {
        className: prefixStyle,
        children: prefix
      }) : PrefixIcon ? /*#__PURE__*/jsx("span", {
        className: prefixIconStyle,
        children: /*#__PURE__*/jsx(PrefixIcon, {
          color: inaccesibleIconColor ?? "gray-50",
          size: iconSize
        })
      }) : null, renderInput ? renderInput({ ...inputProps,
        inputRef
      }) : /*#__PURE__*/jsx(InputTag, {
        ref: inputRef,
        ...inputProps
      }), !options.hideClearButton && /*#__PURE__*/jsx("span", {
        className: clearButtonStyle,
        onClick: _ => {
          const inputEl = inputRef.current;

          if (inputEl) {
            triggerNativeEventFor(inputEl, {
              event: "input",
              value: ""
            });
          }
        },
        children: /*#__PURE__*/jsx(Memo, {
          size: size === "xsmall" ? "sm" : "lg",
          color: "neutral-tertiary-contents"
        })
      }), suffix ? /*#__PURE__*/jsx("span", {
        className: suffixTextStyle,
        children: suffix
      }) : SuffixIcon ? /*#__PURE__*/jsx("span", {
        className: suffixIconStyle,
        children: /*#__PURE__*/jsx(SuffixIcon, {
          color: inaccesibleIconColor ?? "neutral-primary-contents",
          size: iconSize
        })
      }) : null]
    }), hintText ? /*#__PURE__*/jsx(TextVariant, {
      variantKey: hintVariantKey,
      className: hintStyle,
      children: hintText
    }) : null]
  });
});
TextField.displayName = "TextField";
export { TextField };
